let deferredInstallPrompt = null
let listenersRegistered = false
let installedFromEvent = false
const installStateSubscribers = new Set()

const notifyInstallStateChange = () => {
  installStateSubscribers.forEach((callback) => callback())
}

const registerInstallListeners = () => {
  if (listenersRegistered || typeof window === "undefined") {
    return
  }

  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault()
    deferredInstallPrompt = event
    notifyInstallStateChange()
  })

  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null
    installedFromEvent = true
    notifyInstallStateChange()
  })

  listenersRegistered = true
}

export const usePwaHelpers = (controller) => {
  registerInstallListeners()

  // check user-agent for safari on ios
  const isIos = () => {
    let ua = window.navigator.userAgent
    let iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    let webkit = !!ua.match(/WebKit/i)
    let safari = !!ua.match(/Safari/i)
    let notChrome = !ua.match(/CriOS/i)
    let notFirefox = !ua.match(/FxiOS/i)
    return iOS && webkit && safari && notChrome && notFirefox
  }

  const getIosMajorVersion = () => {
    const ua = window.navigator.userAgent

    // iPadOS 13+ identifying as Macintosh
    if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
      const versionMatch = ua.match(/Version\/(\d+)/)
      if (versionMatch) return parseInt(versionMatch[1], 10)
    }

    // Standard iOS/iPhone OS version match
    // Note: We avoid matching "OS X" by ensuring we look for "CPU iPhone OS" or "OS " followed by digits
    const osMatch = ua.match(/(?:CPU(?: iPhone)? OS|OS) (\d+)[._]/)
    if (osMatch && osMatch[1]) return parseInt(osMatch[1], 10)

    return null
  }

  // iOS 26 significantly changed safari's flow for installing PWA
  const isIosPre26 = () => {
    if (!isIos()) return false

    const version = getIosMajorVersion()
    if (version === null) return false

    return version < 26
  }

  const isInstalled = () => {
    if (isIos()) {
      return window.navigator.standalone === true
    } else {
      return window.matchMedia("(display-mode: standalone)").matches
    }
  }

  const isIosNative = () => {
    return !!window.webkit?.messageHandlers?.turbo
  }

  const isAppleDesktop = () => {
    let userAgent = navigator.userAgent.toLowerCase()
    if (navigator.maxTouchPoints || !userAgent.match(/macintosh/)) {
      return false
    }

    const version = /version\/(\d{2})\./.exec(userAgent)
    if (!version || !version[1] || !(parseInt(version[1], 10) >= 17)) {
      return false
    }

    let audioCheck = document.createElement("audio").canPlayType('audio/wav; codecs="1"') ? true : false
    let webGLCheck = new OffscreenCanvas(1, 1).getContext("webgl") ? true : false

    return audioCheck && webGLCheck
  }

  const isAndroidPhone = () => {
    let ua = window.navigator.userAgent
    let android = !!ua.match(/Android/i)
    let chrome = !!ua.match(/Chrome/i)
    return android && chrome
  }

  const isDesktopChrome = () => {
    const ua = window.navigator.userAgent
    const isMobile = /Android|iPhone|iPad|iPod/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)

    return /Chrome\//i.test(ua) && !isMobile && !/Edg|OPR|Opera/i.test(ua)
  }

  const getPlatform = () => {
    if (isIos()) {
      return "ios_safari"
    }

    if (isAndroidPhone()) {
      return "android_chrome"
    }

    if (isDesktopChrome()) {
      return "desktop_chrome"
    }

    if (isAppleDesktop()) {
      return "apple_desktop_safari"
    }

    return "unsupported"
  }

  const canInstall = () => {
    return hasDeferredInstallPrompt() || getPlatform() !== "unsupported"
  }

  const hasDeferredInstallPrompt = () => {
    return deferredInstallPrompt !== null
  }

  const getInstallState = () => {
    if (installedFromEvent || isInstalled() || isIosNative()) {
      return "installed"
    }

    if (hasDeferredInstallPrompt()) {
      return "install_prompt_available"
    }

    if (canInstall()) {
      return "manual_only"
    }

    return "unsupported"
  }

  const promptInstallApp = async () => {
    if (!hasDeferredInstallPrompt()) {
      return false
    }

    const promptEvent = deferredInstallPrompt
    deferredInstallPrompt = null

    promptEvent.prompt()
    await promptEvent.userChoice
    notifyInstallStateChange()

    return true
  }

  const onInstallStateChange = (callback) => {
    installStateSubscribers.add(callback)

    return () => installStateSubscribers.delete(callback)
  }

  Object.assign(controller, {
    isIos,
    isIosPre26,
    isAppleDesktop,
    isAndroidPhone,
    canInstall,
    isInstalled,
    isIosNative,
    getIosMajorVersion,
    getPlatform,
    getInstallState,
    hasDeferredInstallPrompt,
    promptInstallApp,
    onInstallStateChange
  })
}

export const resetPwaHelpersForTests = () => {
  deferredInstallPrompt = null
  installedFromEvent = false
  installStateSubscribers.clear()
}
